import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import GameRecordingPage from './pages/game-recording';
import LiveGamePage from './pages/live-game';
import LandingPage from './pages/landing';
import StatsPage from './pages/stats';
import HistoryPage from './pages/history';
import PlayersManagement from './pages/players-management';
import PlayerProfilePage from './pages/player-profile';
import LoginPage from './pages/login';
import HomePage from './pages/home';
import LeagueHub from './pages/league-hub';
import MembersPage from './pages/members';
import MessagesPage from './pages/messages';
import ConversationPage from './pages/messages/conversation';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LeagueProvider, useLeague } from './contexts/LeagueContext';
import { AppProvider } from './contexts/AppContext';
import { MessagesProvider } from './contexts/MessagesContext';

// Redirects to /login if not authenticated
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Redirects to /leagues if no active league selected
const LeagueRoute = ({ children }) => {
  const { activeLeague, loading } = useLeague();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
      </div>
    );
  }
  if (!activeLeague) return <Navigate to="/leagues" replace />;
  return children;
};

// Redirects authenticated users away from login
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
      </div>
    );
  }
  if (user) return <Navigate to="/leagues" replace />;
  return children;
};

// Renders the public landing for guests; authenticated users see the dashboard.
const RootRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
      </div>
    );
  }
  if (!user) return <LandingPage />;
  return <LeagueRoute><HomePage /></LeagueRoute>;
};

const AppRoutes = () => {
  return (
    <RouterRoutes>
      {/* Public */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />

      {/* Authenticated but no league needed */}
      <Route path="/leagues" element={<ProtectedRoute><LeagueHub /></ProtectedRoute>} />

      {/* Authenticated + league selected */}
      <Route path="/" element={<RootRoute />} />
      <Route path="/stats" element={<ProtectedRoute><LeagueRoute><StatsPage /></LeagueRoute></ProtectedRoute>} />
      <Route path="/game-recording" element={<ProtectedRoute><LeagueRoute><GameRecordingPage /></LeagueRoute></ProtectedRoute>} />
      <Route path="/live-game" element={<ProtectedRoute><LeagueRoute><LiveGamePage /></LeagueRoute></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><LeagueRoute><HistoryPage /></LeagueRoute></ProtectedRoute>} />
      <Route path="/players" element={<ProtectedRoute><LeagueRoute><PlayersManagement /></LeagueRoute></ProtectedRoute>} />
      <Route path="/players/:playerId" element={<ProtectedRoute><LeagueRoute><PlayerProfilePage /></LeagueRoute></ProtectedRoute>} />
      <Route path="/members" element={<ProtectedRoute><LeagueRoute><MembersPage /></LeagueRoute></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><LeagueRoute><MessagesPage /></LeagueRoute></ProtectedRoute>} />
      <Route path="/messages/:userId" element={<ProtectedRoute><LeagueRoute><ConversationPage /></LeagueRoute></ProtectedRoute>} />

      <Route path="*" element={<NotFound />} />
    </RouterRoutes>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <AuthProvider>
          <LeagueProvider>
            <AppProvider>
              <MessagesProvider>
                <AppRoutes />
              </MessagesProvider>
            </AppProvider>
          </LeagueProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
